import { addYears } from "date-fns";
import React from "react";
import Title from "src/components/Title";
import { ILanguageOptionsAvailable } from "src/context/Translations/interface";
import formatDate from "src/utils/formatDate";
import formatNumber from "src/utils/formatNumber";
import { IDataTable, IValuesTable } from "../../interface";
import * as ListStyles from "./styles";

export const formatValue = (value: number, currentLanguage: ILanguageOptionsAvailable, decimalPlaces: number, isPercent?: boolean) => {
	if(value < 0)
		return `(${formatNumber(value.toFixed(decimalPlaces), currentLanguage, decimalPlaces).replace("-","")}${isPercent ? "%" : ""})`;
	 
	return `${formatNumber(value.toFixed(decimalPlaces), currentLanguage, decimalPlaces)}${isPercent ? "%" : ""}`;
};

export const columnPlanned = (row: IDataTable, value: IValuesTable, showVerticalAnalysis: boolean, showHorizontalAnalysis: boolean, showQuantity: boolean, currentLanguage: ILanguageOptionsAvailable, isAcumulated?: boolean) => (
	<>
		<ListStyles.TD className="value-predicted">
			<ListStyles.OverflowSpan 
				red={row.format === "Q" ? value.quantityPredicted < 0 : value.predicted < 0}
				bold={isAcumulated || row.highlightLine}
			>
				{row.format === "Q" ? formatValue(value.quantityPredicted, currentLanguage, 0) : formatValue(value.predicted, currentLanguage, 0)}
			</ListStyles.OverflowSpan>
		</ListStyles.TD>
									
		{showVerticalAnalysis && (
			<ListStyles.TD className="value-predicted">
				{value.verticalPredicted != null && (
					<ListStyles.OverflowSpan 
						purple
						bold={isAcumulated || row.highlightLine}
					>
						{formatValue(value.verticalPredicted, currentLanguage, 1, true)}
					</ListStyles.OverflowSpan>
				)}
			</ListStyles.TD>
		)}

		{showHorizontalAnalysis && (
			<ListStyles.TD className="value-predicted">
				<ListStyles.OverflowSpan 
					purple
					bold={isAcumulated || row.highlightLine}
				>
					{formatValue(value.horizontalPredicted, currentLanguage, 1, true)}
				</ListStyles.OverflowSpan>
			</ListStyles.TD>
		)}

		{showQuantity && (
			<ListStyles.TD className="value-predicted">
				{value.quantityPredicted != null && (
					<ListStyles.OverflowSpan 
						red={value.predicted / value.quantityPredicted < 0}
						bold={isAcumulated || row.highlightLine}
					>
						{row.format === "Q" ?  null : formatValue(value.predicted / value.quantityPredicted, currentLanguage, 2)}
					</ListStyles.OverflowSpan>
				)}
			</ListStyles.TD>
		)}
	</>
);

export const headerColumnPlanned = (value: IValuesTable | undefined, showVerticalAnalysis: boolean, showHorizontalAnalysis: boolean, showQuantity: boolean, currentLanguage: ILanguageOptionsAvailable, translate: (text: string, objKeys?: object | undefined) => string, coinPlannedInitials?: string) => (
	<>
		<ListStyles.TD>
			<ListStyles.DivColumn>
				<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
					{translate("Planejado")}
				</Title>
				{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
				<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
					{!value ? 
						<>
							{translate("Acumulado")}
						</> 
						: 
						<>
							{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
						</>
					}
				</Title>
			</ListStyles.DivColumn>
		</ListStyles.TD>
									
		{showVerticalAnalysis && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("A.V. Plan")}
					</Title>
					{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}

		{showHorizontalAnalysis && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("A.H. Plan")}
					</Title>
					{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}

		{showQuantity && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("Valor")}/{translate("Qtde")}
					</Title>
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}
	</>
);

export const columnRealized = (row: IDataTable, value: IValuesTable, showVerticalAnalysis: boolean, showHorizontalAnalysis: boolean, showQuantity: boolean, showForecast: boolean, currentLanguage: ILanguageOptionsAvailable, isAcumulated?: boolean) => {
	const internalValue = {...value};
	if(showForecast){
		internalValue.realized = value.forecast;
		internalValue.quantityRealized = value.quantityForecast;
		internalValue.horizontalRealized = value.horizontalForecast;
		internalValue.verticalRealized = value.verticalForecast;
	}
	return(
		<>
			<ListStyles.TD className="value-realized">
				<ListStyles.OverflowSpan 
					red={row.format === "Q" ? internalValue.quantityRealized < 0 : internalValue.realized < 0}
					bold={isAcumulated || row.highlightLine}
				>
					{row.format === "Q" ? formatValue(internalValue.quantityRealized, currentLanguage, 0) : formatValue(internalValue.realized, currentLanguage, 0)}
				</ListStyles.OverflowSpan>
			</ListStyles.TD>
			{showVerticalAnalysis && (
				<ListStyles.TD className="value-realized">
					{internalValue.verticalRealized !== null && (
						<ListStyles.OverflowSpan 
							purple
							bold={isAcumulated || row.highlightLine}
						>
							{formatValue(internalValue.verticalRealized, currentLanguage, 1, true)}
						</ListStyles.OverflowSpan>
					)}
				</ListStyles.TD>
			)}

			{showHorizontalAnalysis && (
				<ListStyles.TD className="value-realized">
					<ListStyles.OverflowSpan 
						purple
						bold={isAcumulated || row.highlightLine}
					>
						{formatValue(internalValue.horizontalRealized, currentLanguage, 1)}
					</ListStyles.OverflowSpan>
				</ListStyles.TD>
			)}

			{showQuantity && (
				<ListStyles.TD className="value-realized">
					{internalValue.realized !== null &&  (
						<ListStyles.OverflowSpan 
							red={internalValue.realized / internalValue.quantityRealized < 0}
							bold={isAcumulated || row.highlightLine}
						>
							{row.format === "Q" ?  null : formatValue(internalValue.realized / internalValue.quantityRealized, currentLanguage, 2)}
						</ListStyles.OverflowSpan>
					)}
				</ListStyles.TD>
			)}
		</>
	);};

export const headerColumnRealized = (value: IValuesTable | undefined, showVerticalAnalysis: boolean, showHorizontalAnalysis: boolean, showQuantity: boolean, showForecast: boolean, currentLanguage: ILanguageOptionsAvailable, translate: (text: string, objKeys?: object | undefined) => string, showPlannedString = false, coinPlannedInitials?: string, coinRealizedInitials?: string) => (
	<>
		<ListStyles.TD>
			<ListStyles.DivColumn>
				<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
					{!showPlannedString ? 
						<>
							{showForecast ? "Forecast" : translate("Realizado")}
						</>: 
						<>
							{translate("Planejado")}
						</>
					}
				</Title>
				{!showPlannedString ? <>
					{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
				</> : <>
					{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
				</>}
				<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
					{!value ? 
						<>
							{translate("Acumulado")}
						</> 
						: 
						<>
							{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
						</>
					}
				</Title>
			</ListStyles.DivColumn>
		</ListStyles.TD>
									
		{showVerticalAnalysis && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("A.V. Real")}
					</Title>
					{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}

		{showHorizontalAnalysis && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("A.H. Real")}
					</Title>
					{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}

		{showQuantity && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("Valor")}/{translate("Qtde")}
					</Title>
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(value.date, currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}
	</>
);

export const columnLastYear = (row: IDataTable, value: IValuesTable, showVerticalAnalysis: boolean, showHorizontalAnalysis: boolean, showQuantity: boolean, currentLanguage: ILanguageOptionsAvailable, isAcumulated?: boolean) => (
	<>
		<ListStyles.TD className="value-last-year">
			<ListStyles.OverflowSpan 
				red={row.format === "Q" ? value.quantityLastYear < 0 : value.lastYear < 0}
				bold={isAcumulated || row.highlightLine}
			>
				{row.format === "Q" ? formatValue(value.quantityLastYear, currentLanguage, 0) : formatValue(value.lastYear, currentLanguage, 0)}
			</ListStyles.OverflowSpan>
		</ListStyles.TD>
		{showVerticalAnalysis && (
			<ListStyles.TD className="value-last-year">
				{value.verticalLastYear !== null && (
					<ListStyles.OverflowSpan 
						purple
						bold={isAcumulated || row.highlightLine}
					>
						{formatValue(value.verticalLastYear, currentLanguage, 1, true)}
					</ListStyles.OverflowSpan>
				)}
			</ListStyles.TD>
		)}

		{showHorizontalAnalysis && (
			<ListStyles.TD className="value-last-year">
				<ListStyles.OverflowSpan 
					purple
					bold={isAcumulated || row.highlightLine}
				>
					{formatValue(value.horizontalLastYear, currentLanguage, 1, true)}
				</ListStyles.OverflowSpan>
			</ListStyles.TD>
		)}

		{showQuantity && (
			<ListStyles.TD className="value-last-year">
				{value.quantityLastYear !== null && (
					<ListStyles.OverflowSpan 
						red={value.lastYear / value.quantityLastYear < 0}
						bold={isAcumulated || row.highlightLine}
					>
						{row.format === "Q" ?  null : formatValue(value.lastYear / value.quantityLastYear, currentLanguage, 2)}
					</ListStyles.OverflowSpan>
				)}
			</ListStyles.TD>
		)}
	</>
);

export const headerColumnLastYear = (value: IValuesTable | undefined, showVerticalAnalysis: boolean, showHorizontalAnalysis: boolean, showQuantity: boolean, currentLanguage: ILanguageOptionsAvailable, translate: (text: string, objKeys?: object | undefined) => string, coinRealizedInitials?: string) => (
	<>
		<ListStyles.TD>
			<ListStyles.DivColumn>
				<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
					{translate("Anterior")}
				</Title>
				{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
				<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
					{!value ? 
						<>
							{translate("Acumulado")}
						</> 
						: 
						<>
							{value.date != null ? formatDate(addYears(new Date(value.date), -1), currentLanguage, false, "dd/") : ""}
						</>
					}
				</Title>
			</ListStyles.DivColumn>
		</ListStyles.TD>
									
		{showVerticalAnalysis && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("A.V. Anterior")}
					</Title>
					{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(addYears(new Date(value.date), -1), currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}

		{showHorizontalAnalysis && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("A.H. Anterior")}
					</Title>
					{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(addYears(new Date(value.date), -1), currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}

		{showQuantity && (
			<ListStyles.TD>
				<ListStyles.DivColumn>
					<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
						{translate("Valor")}/{translate("Qtde")}
					</Title>
					<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
						{!value ? 
							<>
								{translate("Acumulado")}
							</> 
							: 
							<>
								{value.date != null ? formatDate(addYears(new Date(value.date), -1), currentLanguage, false, "dd/") : ""}
							</>
						}
					</Title>
				</ListStyles.DivColumn>
			</ListStyles.TD>
		)}
	</>
);
